import { Skeleton } from "@tellescope/ui";
import { Card } from "@tellescope/design-system";
import { formatDate } from "@tellescope/utils";
import { useTranslation } from "react-i18next";
import { Package, Truck, Clock, CheckCircle, AlertCircle } from "lucide-react";
import type { EnduserOrder } from "@tellescope/types-models";
import type { OrderListBlock as OrderListBlockType } from "../../../shared/schema";
import { MobileList } from "../MobileList";
import { useEnduserOrders } from "../../hooks/useEnduserOrders";
import { useOrdersFilter } from "../../hooks/useItemFilter";

interface OrderListBlockProps {
  block: OrderListBlockType;
}

interface OrderCardProps {
  order: EnduserOrder;
  customTrackingUrl?: string;
}

// Build tracking URL with fallback logic
// Exported for testing
export function getTrackingUrl(tracking: string, customTrackingUrl?: string): string {
  // If tracking is already a URL, use it directly
  if (tracking.startsWith("http")) {
    return tracking;
  }
  // If custom template is configured, use it
  if (customTrackingUrl) {
    return customTrackingUrl.replace("{{tracking}}", tracking);
  }
  // Default to Google search
  return `https://www.google.com/search?q=${encodeURIComponent(tracking)}`;
}

// Map status to icon and color
function getStatusInfo(status: string): { icon: React.ElementType; colorClass: string } {
  const normalizedStatus = status.toLowerCase();

  if (normalizedStatus.includes("deliver") || normalizedStatus.includes("complete")) {
    return { icon: CheckCircle, colorClass: "text-green-600" };
  }
  if (normalizedStatus.includes("ship") || normalizedStatus.includes("transit")) {
    return { icon: Truck, colorClass: "text-blue-600" };
  }
  if (normalizedStatus.includes("pending") || normalizedStatus.includes("process")) {
    return { icon: Clock, colorClass: "text-yellow-600" };
  }
  if (normalizedStatus.includes("error") || normalizedStatus.includes("cancel") || normalizedStatus.includes("fail")) {
    return { icon: AlertCircle, colorClass: "text-red-600" };
  }

  return { icon: Package, colorClass: "text-primary" };
}

function OrderCard({ order, customTrackingUrl }: OrderCardProps) {
  const { t } = useTranslation("device-orders");
  const statusInfo = getStatusInfo(order.status);
  const StatusIcon = statusInfo.icon;

  return (
    <Card variant="floating" padding="root" className="overflow-hidden">
      <Card.Body className="px-4">
        <div className="flex gap-4">
          {/* Status icon */}
          <div className="flex-shrink-0 text-center">
            <div className="w-14 h-14 rounded-lg bg-primary/10 flex flex-col items-center justify-center">
              <StatusIcon className={`h-6 w-6 ${statusInfo.colorClass}`} />
            </div>
          </div>

          {/* Order details */}
          <div className="flex-1 min-w-0">
            <h4 className="font-medium text-sm truncate">{order.title}</h4>

            <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
              <span className="capitalize">{order.status}</span>
            </div>

            {order.description && (
              <div className="text-xs text-muted-foreground mt-1 line-clamp-1">{order.description}</div>
            )}

            {order.tracking && (
              <a
                href={getTrackingUrl(order.tracking, customTrackingUrl)}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-xs text-primary mt-1 hover:underline"
              >
                <Truck className="h-3 w-3" />
                <span>
                  {t("Tracking")}: {order.tracking}
                </span>
              </a>
            )}

            <div className="text-xs text-muted-foreground mt-1">{formatDate(order.updatedAt)}</div>
          </div>
        </div>
      </Card.Body>
    </Card>
  );
}

function OrderListSkeleton({ count = 3 }: { count?: number }) {
  return (
    <div className="space-y-2">
      {Array.from({ length: count }).map((_, i) => (
        <Card variant="floating" padding="root" key={i}>
          <Card.Body className="px-4">
            <div className="flex gap-4">
              <Skeleton className="w-14 h-14 rounded-lg" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-3 w-1/2" />
                <Skeleton className="h-3 w-1/3" />
              </div>
            </div>
          </Card.Body>
        </Card>
      ))}
    </div>
  );
}

export function OrderListBlock({ block }: OrderListBlockProps) {
  const { t } = useTranslation("device-orders");
  const { itemFilter } = block.props;

  const { orders: rawOrders, isLoading: ordersLoading, error } = useEnduserOrders();

  // Apply item filter if configured
  const { filteredItems: orders, isLoading: filterLoading } = useOrdersFilter({
    items: rawOrders,
    itemFilter,
  });

  const isLoading = ordersLoading || filterLoading;

  if (isLoading) {
    return <OrderListSkeleton count={block.props.maxItems ?? 5} />;
  }

  if (error) {
    return (
      <Card variant="floating" padding="root" className="border-destructive">
        <Card.Body className="p-4 text-center text-destructive">{t("Failed to load orders")}</Card.Body>
      </Card>
    );
  }

  return (
    <MobileList
      items={orders}
      renderItem={(order) => <OrderCard order={order} customTrackingUrl={block.props.customTrackingUrl} />}
      title={t("Your Orders")}
      maxItems={block.props.maxItems ?? 5}
      emptyMessage={block.props.emptyMessage ?? t("No orders")}
    />
  );
}
